import { PlusIcon } from "@radix-ui/react-icons";
import { TabsContent } from "@radix-ui/react-tabs";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { DollarSignIcon } from "lucide-react";

import { Page } from "@/components/Page";
import { ButtonLink } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { H1 } from "@/components/ui/typography";
import { campaignNotificationsQueryOptions } from "@/lib/api";

export const Route = createFileRoute("/app/_sidebar-layout/_campaigns-layout")({
  component: LayoutComponent,
  loader: ({ context: { queryClient } }) => {
    void Promise.all([
      queryClient.prefetchQuery(campaignNotificationsQueryOptions()),
    ]);
  },
  head: () => ({ meta: [{ title: "Campaigns" }] }),
});

function LayoutComponent() {
  const router = useRouterState();
  const { data: notifications } = useSuspenseQuery(
    campaignNotificationsQueryOptions(),
  );

  const activeNotifications = notifications.filter(
    (n) => n.category === "incoming_proposed_campaign",
  );
  const outgoingNotifications = notifications.filter(
    (n) => n.category === "outgoing_accepted_campaign",
  );

  return (
    <Page className="gap-4 md:gap-6 lg:gap-10">
      <div className="flex items-center justify-between gap-2">
        <H1>Campaigns</H1>
        <div className="flex items-center gap-2">
          <ButtonLink
            to="/app/payments/history"
            variant="outline"
            size="icon"
            className="lg:hidden"
          >
            <DollarSignIcon />
            <span className="sr-only">Payment History</span>
          </ButtonLink>
          <ButtonLink
            size="icon"
            to="/app/campaigns/create"
            className="lg:hidden"
          >
            <PlusIcon />
            <span className="sr-only">New Campaign</span>
          </ButtonLink>
          <ButtonLink
            to="/app/payments/history"
            variant="outline"
            className="hidden lg:flex"
          >
            <div className="flex items-center gap-2">
              <DollarSignIcon />
              Payment History
            </div>
          </ButtonLink>
          <ButtonLink to="/app/campaigns/create" className="hidden lg:flex">
            <div className="flex items-center gap-2">
              <PlusIcon />
              New Campaign
            </div>
          </ButtonLink>
        </div>
      </div>
      <div className="flex flex-col gap-6 -mx-4 w-[calc(100%+32px)] md:mx-auto md:w-full">
        <Tabs
          value="active"
          className="md:border border-slate-6 dark:border-slatedark-6 rounded-md md:shadow-md"
        >
          <TabsList
            variant="outline-solid"
            className="border-b border-slate-6 dark:border-slatedark-6 w-full"
          >
            <TabsTrigger
              variant="outline-solid"
              value={
                router.location.pathname.endsWith("/campaigns") ? "active" : ""
              }
              asChild
            >
              <Link to="/app/campaigns">
                Active
                {outgoingNotifications?.length > 0 && (
                  <div className="px-1 py-0.5 text-xs bg-red-11 dark:bg-reddark-9 text-slate-1 dark:text-slatedark-1 rounded-md">
                    {outgoingNotifications.length}
                  </div>
                )}
              </Link>
            </TabsTrigger>
            <TabsTrigger
              variant="outline-solid"
              value={
                router.location.pathname.endsWith("/requests") ? "active" : ""
              }
              asChild
            >
              <Link to="/app/campaigns/requests">
                Requests
                {activeNotifications?.length > 0 && (
                  <div className="px-1 py-0.5 text-xs bg-red-11 dark:bg-reddark-9 text-slate-1 dark:text-slatedark-1 rounded-md">
                    {activeNotifications.length}
                  </div>
                )}
              </Link>
            </TabsTrigger>
            <TabsTrigger
              variant="outline-solid"
              value={
                router.location.pathname.endsWith("/history") ? "active" : ""
              }
              asChild
            >
              <Link to="/app/campaigns/history">History</Link>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="active">
            <Outlet />
          </TabsContent>
        </Tabs>
      </div>
    </Page>
  );
}
